/**
 * BookingService
 * Gestisce le chiamate API per il sistema di prenotazione:
 * - Recupero attività/prestazioni disponibili
 * - Recupero risorse/medici
 * - Ricerca disponibilità slot appuntamenti
 */

import { BaseService } from './BaseService';
import logger from '../Logger';

// ============================================================================
// INTERFACES
// ============================================================================

/**
 * Attività/Prestazione disponibile per prenotazione
 * Supporta sia formato backend NMS (minuscolo) che legacy (UPPERCASE)
 */
export interface Activity {
  // Formato NMS (minuscolo)
  id?: string;
  name?: string;
  group?: string;
  groupId?: string;
  notice?: string;
  preparation?: string;
  duration?: number;
  price?: number;
  webEnabled?: number;
  // Formato Legacy (UPPERCASE)
  ACTIVITY_LID?: string;
  ACTIVITY_NAME?: string;
  ACTIVITY_GROUP_NAME?: string;
  ACTIVITY_GROUP_LID?: string;
  ACTIVITY_NOTICE?: string;
  ACTIVITY_PREPARATION?: string;
  ACTIVITY_DURATION?: number;
  ACTIVITY_PRICE?: number;
  WEB_ENABLED?: number;
}

/**
 * Risorsa/Medico disponibile per prenotazioni
 */
export interface Resource {
  RESOURCE_LID: string;
  LOCATION_LID: string;
  RESOURCE_ID_NUMBER: string;
  RESOURCE_FIRST_NAME: string;
  RESOURCE_SECOND_NAME: string;
  RESOURCE_NOTICE?: string;
  RESOURCE_EMAIL?: string;
  RESOURCE_MOBILE_PHONE?: string;
  WEB_ENABLED: number;
}

/**
 * Disponibilità slot per appuntamento
 */
export interface Availability {
  AVAILABILITY_LID: string;
  AVA_DATE: string;
  AVA_START_TIME: string;
  AVA_END_TIME: string;
  RESOURCE_LID: string;
  ACTIVITY_LID: string;
  INSURANCE_LID: string;
  LOCATION_LID: string;
  AVA_PRICE?: number;
}

/**
 * Parametri per ricerca disponibilità
 */
export interface SearchAvailabilitiesParams {
  activityId: string;
  startDate: Date;
  endDate: Date;
  resourceId?: string;
  locationId?: string;
  insuranceId?: string;
  minTime?: string;
  maxTime?: string;
  resultsNumber?: number;
}

/**
 * Formato risposta API standard del backend
 * NOTA: Il backend usa campi in minuscolo (return, msg, result)
 */
interface ApiResponse<T> {
  return: T;
  Return?: T; // Legacy: alcuni endpoint potrebbero ancora usare maiuscolo
  msg?: string;
  Msg?: string;
  result: 'OK' | 'KO';
  Result?: 'OK' | 'KO';
}

// ============================================================================
// SERVICE CLASS
// ============================================================================

class BookingService extends BaseService {
  private static instance: BookingService;

  private constructor() {
    // Per BookingService usiamo "/" come baseURL perché le chiamate clientAPI
    // NON devono avere il prefisso /api/ (che viene aggiunto solo per le API standard)
    // Le rotte clientAPI sono gestite direttamente: /clientAPI/getactivities
    super({ baseURL: '/' });
  }

  /**
   * Ottiene istanza singleton del servizio
   */
  static getInstance(): BookingService {
    if (!BookingService.instance) {
      BookingService.instance = new BookingService();
      logger.info('BookingService initialized');
    }
    return BookingService.instance;
  }

  // ==========================================================================
  // PUBLIC METHODS
  // ==========================================================================

  /**
   * Recupera lista completa attività/prestazioni disponibili
   * @param resourceId - (Opzionale) Filtra per risorsa specifica
   * @param insuranceId - (Opzionale) Filtra per assicurazione
   * @returns Array di Activity
   */
  async getActivities(resourceId?: string, insuranceId?: string): Promise<Activity[]> {
    try {
      logger.debug('BookingService.getActivities', { resourceId, insuranceId });

      const params: Record<string, string> = {};
      if (resourceId) params.resource_lid = resourceId;
      if (insuranceId) params.insurance_lid = insuranceId;

      const queryString = Object.keys(params).length > 0
        ? '?' + new URLSearchParams(params).toString()
        : '';

      const response = await this.get<ApiResponse<Activity[]>>(
        `clientAPI/getactivities${queryString}`
      );

      // Debug: log raw response
      console.log('🔍 Raw API response:', JSON.stringify(response).substring(0, 500));
      console.log('🔍 Response keys:', Object.keys(response));

      // Supporta sia minuscolo (nuovo) che maiuscolo (legacy)
      const result = response.result || response.Result;
      const returnData = response.return || response.Return;
      const msg = response.msg || response.Msg;

      console.log('🔍 Parsed: result=', result, 'returnData length=', returnData?.length, 'msg=', msg);

      if (result === 'KO') {
        throw this.createError(
          msg || 'Errore durante il recupero delle attività',
          400
        );
      }

      logger.info('Activities loaded successfully', { count: returnData?.length || 0 });
      return returnData || [];

    } catch (error) {
      logger.error('Failed to load activities', error);
      throw error;
    }
  }

  /**
   * Recupera lista risorse/medici disponibili
   * @param activityId - (Opzionale) Filtra per attività specifica
   * @param locationId - (Opzionale) Filtra per sede
   * @param insuranceId - (Opzionale) Filtra per assicurazione
   * @returns Array di Resource
   */
  async getResources(
    activityId?: string,
    locationId?: string,
    insuranceId?: string
  ): Promise<Resource[]> {
    try {
      logger.debug('BookingService.getResources', { activityId, locationId, insuranceId });

      const params: Record<string, string> = {};
      if (activityId) params.activity_lid = activityId;
      if (locationId) params.location_lid = locationId;
      if (insuranceId) params.insurance_lid = insuranceId;

      const queryString = Object.keys(params).length > 0
        ? '?' + new URLSearchParams(params).toString()
        : '';

      const response = await this.get<ApiResponse<Resource[]>>(
        `clientAPI/getresources${queryString}`
      );

      // Supporta sia minuscolo (nuovo) che maiuscolo (legacy)
      const result = response.result || response.Result;
      const returnData = response.return || response.Return;
      const msg = response.msg || response.Msg;

      if (result === 'KO') {
        throw this.createError(
          msg || 'Errore durante il recupero delle risorse',
          400
        );
      }

      logger.info('Resources loaded successfully', { count: returnData?.length || 0 });
      return returnData || [];

    } catch (error) {
      logger.error('Failed to load resources', error);
      throw error;
    }
  }

  /**
   * Cerca disponibilità slot per appuntamenti
   * @param params - Parametri di ricerca
   * @returns Array di Availability
   */
  async searchAvailabilities(params: SearchAvailabilitiesParams): Promise<Availability[]> {
    try {
      logger.debug('BookingService.searchAvailabilities', params);

      // Validazione parametri obbligatori
      if (!params.activityId) {
        throw this.createError('Activity ID è obbligatorio', 400);
      }
      if (!params.startDate || !params.endDate) {
        throw this.createError('Date di inizio e fine sono obbligatorie', 400);
      }

      // Costruzione parametri query
      const searchParams = new URLSearchParams({
        activity_lid: params.activityId,
        ava_results_number: (params.resultsNumber || 100).toString(),
        resource_lid: params.resourceId || '',
        ava_min_time: params.minTime || '00:01:00',
        ava_max_time: params.maxTime || '23:59:00',
        ava_start_day: this.formatDate(params.startDate),
        ava_end_day: this.formatDate(params.endDate),
        ava_start_time: '00:01:00',
        ava_end_time: '23:59:00',
        insurance_lid: params.insuranceId || 'PVT',
        location_lid: params.locationId || '',
      });

      const response = await this.get<ApiResponse<Availability[]>>(
        `clientAPI/searchavailabilities?${searchParams.toString()}`
      );

      // Supporta sia minuscolo (nuovo) che maiuscolo (legacy)
      const result = response.result || response.Result;
      const returnData = response.return || response.Return;
      const msg = response.msg || response.Msg;

      if (result === 'KO') {
        throw this.createError(
          msg || 'Errore durante la ricerca delle disponibilità',
          400
        );
      }

      const availabilities = returnData || [];
      logger.info('Availabilities found', {
        count: availabilities.length,
        activityId: params.activityId
      });

      return availabilities;

    } catch (error) {
      logger.error('Failed to search availabilities', error);
      throw error;
    }
  }

  // ==========================================================================
  // HELPER METHODS
  // ==========================================================================

  /**
   * Formatta data in formato YYYY-MM-DD per API backend
   */
  private formatDate(date: Date): string {
    const pad = (n: number) => (n < 10 ? `0${n}` : n);
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
  }

  /**
   * Raggruppa attività per gruppo
   * Utile per visualizzazione in Dropdown gerarchici
   */
  groupActivitiesByGroup(activities: Activity[]): { label: string; items: Activity[] }[] {
    const grouped: { [key: string]: { label: string; items: Activity[] } } = {};

    activities.forEach((activity) => {
      const groupName = activity.ACTIVITY_GROUP_NAME || 'Altro';

      if (!grouped[groupName]) {
        grouped[groupName] = {
          label: groupName,
          items: []
        };
      }

      grouped[groupName].items.push(activity);
    });

    return Object.values(grouped);
  }

  /**
   * Raggruppa disponibilità per data
   * Utile per visualizzazione in Accordion/Timeline
   */
  groupAvailabilitiesByDate(availabilities: Availability[]): { [date: string]: Availability[] } {
    return availabilities.reduce((groups, availability) => {
      const date = availability.AVA_DATE;
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(availability);
      return groups;
    }, {} as { [date: string]: Availability[] });
  }

  /**
   * Formatta disponibilità per display (es. "09:00 - 09:30")
   */
  formatAvailabilityTime(availability: Availability): string {
    return `${availability.AVA_START_TIME} - ${availability.AVA_END_TIME}`;
  }
}

// Export singleton instance
export default BookingService.getInstance();
